/**
 * The state shapes (web-plan.md F1) — the web twin of the desktop's
 * `state/` + `runtime/mod.rs` UiState, carrying exactly what F1–F3 screens
 * read. Slices are deliberately flat and JSON-ish: Immer shares unchanged
 * plain objects and arrays across snapshots, and every rule from the porting contract
 * (dedup by seq, unread as a formula, snapshot-not-delta) is written against
 * these shapes in `mutators.ts`.
 */
import type {
  Call,
  ChannelCard,
  ChannelSettings,
  ChatCommand,
  FriendsSnapshot,
  LinkPreview,
  Message,
  PetAbilities,
  PetCard,
  ReactionGroup,
  UserStatus,
} from "../gen/dto";
import type { Preview } from "../lib/preview";

/** The optimistic bubble's sort key: above every real seq, below nothing.
 * (The Rust client uses u64::MAX; seqs here are JSON numbers and stay far
 * under 2^53, so MAX_SAFE_INTEGER is the same statement.) */
export const PENDING_SEQ = Number.MAX_SAFE_INTEGER;

export const WINDOW_SIZE = 300;
export const PAGE_SIZE = 50;
export const TYPING_SEND_PERIOD_MS = 5_500;
export const TYPING_EXPIRY_MS = 8_000;
export const TICK_MS = 2_000;
export const DIRECTORY_TTL_MS = 30_000;
export const AFK_AFTER_MS = 300_000;
export const RING_FOR_MS = 30_000;
export const BOTTOM_SLACK = 48;
export const TOP_TRIGGER = 240;
export const SETTLE_MS = 700;
/** How long the reading position has to hold still before what it shows counts
 * as read (D249): a pause of the eye, not a wheel notch on the way past — and
 * what keeps a flick through a hundred messages down to a handful of marks. */
export const SEEN_SETTLE_MS = 250;
/** How far under the floating header the arrow down puts the first unread
 * message (dioxus-plan.md D408): room for the last read lines above it. The
 * desktop's `UNREAD_LANDING_PX`, where a channel opened on its backlog lands. */
export const UNREAD_LANDING_PX = 72;
/** How many of the newest readings of the gateway's clock the offset is taken
 * over (D298): enough for one of them to have crossed a quiet network, few
 * enough that a clock set right mid-session is believed within eight messages. */
export const CLOCK_SAMPLES = 8;
/** The most messages one gesture forwards (docs/forwarding.md D377) —
 * `echo_wire::dto::MAX_FORWARD`, Telegram's own ceiling for one forward. */
export const MAX_FORWARD = 100;
/** The most messages one selection holds, and so one deletion takes
 * (docs/message-selection.md D379, D380) — `echo_wire::dto::MAX_SELECTED`. */
export const MAX_SELECTED = 100;
/** How long after its send a message may still be edited (chat-plan.md D409):
 * an hour, on the gateway's clock — `echo_wire::dto::EDIT_WINDOW_MS`. Past it
 * the gateway refuses the edit, and this client offers none. */
export const EDIT_WINDOW_MS = 60 * 60 * 1000;
/** How long after its send its author's deletion of a message still takes it
 * from everybody (chat-plan.md D410): four hours, on the gateway's clock —
 * `echo_wire::dto::DELETE_WINDOW_MS`. Past it the gateway hides the message
 * from its author alone, and this client says «Delete for me». */
export const DELETE_WINDOW_MS = 4 * 60 * 60 * 1000;

export type SendState = "sent" | "preparing" | "sending" | "failed";

export interface Bubble {
  /** Rendering identity survives adoption of the server sequence number. */
  uiKey: string;
  message: Message;
  senderName: string;
  isOwn: boolean;
  /** Computed by regroup(): whether this bubble starts a visual group. */
  showHeader: boolean;
  state: SendState;
  sendProgress: number;
  /** Object URLs shown before the upload finishes (F2). */
  localPreviews: string[];
  /** A send of our own that waited off the feed while its files went up
   * (chat-plan.md D398) and joined it after: it does not take the feed to
   * its end — the press did (`FeedState.withheldSends`), and by now the
   * reader may have gone up to read. */
  withheld?: boolean;
  /** The header over a reply (D273), from the quote the reply itself carries
   * (`message.reply_to`): who wrote what it answers — resolveNames() — and
   * the start of what that says — fillReplyPreviews(), its spoilers still
   * hidden (D343). */
  replyAuthor: string | null;
  replyPreview: Preview | null;
  /** What it answers was deleted: the header stays, says so, and is nothing
   * to press. A reply with neither words nor this flag carries nothing
   * readable about its target and draws a header without words. */
  replyGone: boolean;
  /** Who wrote a forwarded message's words first (forwarding.md D376): the
   * name on its «Forwarded from» line — resolveNames(), from the header the
   * gateway wrote (`message.forwarded`). Null on a message its sender wrote,
   * and on a forward whose header this build could not read, which still
   * draws the line without a name. */
  forwardAuthor: string | null;
  /** Reactions live BESIDE the message (D147): an edit echo replaces the
   * message whole and the chips must survive it. */
  reactions: ReactionGroup[];
  myReaction: string | null;
}

export type ComposerMode =
  | { t: "new" }
  | { t: "replying"; seq: number }
  | {
      t: "editing";
      seq: number;
    };

export interface FeedState {
  channelId: number; // 0 = empty pane
  channelName: string;
  bubbles: Bubble[];
  maxSeq: number;
  lastReadSeq: number;
  reachedStart: boolean;
  /** The window holds the channel's end: a page said so and nothing arrived
   * past it since (the desktop's `Feed::reached_end`, D271). Then a seq
   * between the newest bubble and `maxSeq` is a deleted message, never a
   * missed one — which is what lets the read mark reach the ceiling (D277). */
  reachedEnd: boolean;
  loadingOlder: boolean;
  /** Between open() and the first page: "no messages" and "not arrived yet"
   * must not draw alike. */
  loading: boolean;
  /** The seq the unread separator sits ABOVE, or null. */
  dividerBefore: number | null;
  mode: ComposerMode;
  /** Every new reply/edit and channel opening invalidates deferred composer cleanup. */
  composerRevision: number;
  /** Counts the sends pressed here whose bubble waits off the feed behind the
   * pill (D398): each takes the feed to its end at the press, where a send
   * shown at once takes it by appearing there. */
  withheldSends: number;
  /** The messages picked in this window (forwarding.md D377), ascending by
   * seq, as they were when picked. While anything is here the feed is
   * selecting: a click on a message picks it or puts it back, and the
   * composer's place is the bar that forwards them. Copies and not numbers,
   * so a pick survives the window being trimmed under it; a deletion still
   * takes its message out. openFeed() empties it. */
  selected: Message[];
  error: string | null;
  /** user_id -> last typing frame (ms). Iterated in ascending id order. */
  typing: Record<number, number>;
  lastTypingSentMs: number;
}

export function emptyFeed(): FeedState {
  return {
    channelId: 0,
    channelName: "",
    bubbles: [],
    maxSeq: 0,
    lastReadSeq: 0,
    reachedStart: false,
    reachedEnd: false,
    loadingOlder: false,
    loading: false,
    dividerBefore: null,
    mode: { t: "new" },
    composerRevision: 0,
    withheldSends: 0,
    selected: [],
    error: null,
    typing: {},
    lastTypingSentMs: 0,
  };
}

/** Messages on their way to a chat (forwarding.md D377): picked in one channel
 * and waiting over the composer of another — or of the same one — until the
 * reader sends them or lets them go, as Telegram's forward bar waits. The
 * messages themselves and not their numbers: the channel they were picked in
 * closes when the one they go to opens, and the bar says what it is about to
 * send. Each send still names only where its message stands (`forward`). */
export interface ForwardDraft {
  /** The channel the messages are in: what each send names as its source. */
  from: number;
  /** The channel they go to: the one whose composer the bar stands over. */
  to: number;
  /** Ascending by seq — the order they were said in, and go out in. */
  messages: Message[];
}

export interface Room {
  channelId: number;
  ownerId: number;
  kind: string;
  peerId: number | null;
  name: string;
  description: string | null;
  avatarFileId: string | null;
  /** The line under the name: the newest message, its spoilers still hidden
   * (D343). */
  preview: Preview;
  /** The seq of the message `preview` quotes, 0 for none — what an edit or a
   * deletion is matched against. Not `maxSeq`: once the newest message is
   * deleted the line quotes an older one (D253). */
  previewSeq: number;
  /** When the quoted message goes (its `expires_at_ms`, the gateway's clock),
   * or null for one that stays: the line is a copy of its words, and has to
   * let go of them at the same deadline the chat does (D298). */
  previewExpiresAtMs: number | null;
  timeText: string;
  unread: number;
  maxSeq: number;
  lastReadSeq: number;
  /** Seqs past the read mark this client saw deleted (D277): the badge's
   * subtraction counts them, and refreshUnread() takes them back off. */
  holes: number[];
  lastMessageAtMs: number;
  callMembers: string[];
  callMemberIds: number[];
  member: boolean;
  admin: boolean;
  settings: ChannelSettings;
  members: number;
  joining: boolean;
  /** The reader stopped the pet this dialog is with (pets-plan.md D368): the
   * composer gives way to one Restart button. From `pet_stopped` on the
   * `GET /v1/state` row, kept current by the frame of that name; false on
   * every other row and from a gateway without pets. */
  petStopped: boolean;
}

export function isDmRoom(room: Room): boolean {
  return room.kind === "dm";
}

/** A former friend's conversation (friends-plan.md D411): a direct row nobody
 * is a member of any more — what the gateway keeps listing after an unfriend
 * when there was anything in it. Readable, never writable, on All alone, and
 * the one row that offers «Delete chat» (D412). */
export function isFormerDm(room: Room): boolean {
  return isDmRoom(room) && !room.member;
}

/**
 * A group (D274): a channel whose door is shut for good. It sits on the
 * Channels tab like any channel; only its settings say it cannot be opened.
 */
export function isGroupRoom(room: Room): boolean {
  return room.kind === "group";
}

/** The strip over the list (D179, pets-plan.md D361). `pets` is drawn only on
 * a gateway that knows pets (`AccountState.pets`). */
export type SidebarTab = "all" | "channels" | "friends" | "pets";

export interface Person {
  username: string;
  displayName: string | null;
  bio: string | null;
  avatarFileId: string | null;
  /** Unix seconds — the avatar colour seed's second half (D73). */
  createdAt: number;
  bubbleLight: string | null;
  bubbleDark: string | null;
  /** `pet` for a pet (pets-plan.md D349); absent — or a word this build does
   * not know — is a person, which is what a client older than pets draws
   * every account as (§10). */
  kind?: string;
  /** A pet's revision (D360): moves with every change to its card, its
   * commands above all. Absent for a person. */
  petRev?: number;
  /** The pet has been retired (D365): search skips it, its dialog shows a
   * notice where the composer was. */
  retired?: boolean;
}

/** Whether the account is a pet (D349) — everything that draws a paw, skips
 * a friendship or offers a pet's verbs asks this, and nothing else. */
export function isPet(person: Person | null | undefined): boolean {
  return person?.kind === "pet";
}

export interface DirectoryState {
  people: Record<number, Person>;
  selfId: number;
  selfIdFallback: number;
  lastRefreshMs: number | null;
  refreshWanted: boolean;
}

export interface FriendsState extends FriendsSnapshot {
  /** The first snapshot seeds silently — no toasts for a world that merely
   * already existed (D174). */
  seeded: boolean;
}

export interface PresenceState {
  /** Sparse: absent = offline (and an unknown status draws nothing). */
  map: Record<number, UserStatus>;
  /** The last AFK state we REPORTED — the hysteresis for activity frames. */
  selfAfk: boolean;
  /** Set solely by the presence key's presence in GET /v1/state — the
   * capability probe that gates ever sending an activity frame. */
  enabled: boolean;
}

export interface AccountState {
  displayName: string | null;
  bio: string | null;
  avatarFileId: string | null;
  createdAt: number;
  bubbleLight: string | null;
  bubbleDark: string | null;
  manualStatus: "dnd" | "invisible" | null;
  isAdmin: boolean;
  /** Pets on this server (pets-plan.md §10, D363) — `pets` of `GET /v1/me`,
   * the capability probe, and read off that answer alone. `null` is a
   * gateway that does not know pets: the Pets tab and every pet control stay
   * hidden rather than offering what the server cannot do (the D274 rule).
   * `create` says whether this account may make one now. */
  pets: PetAbilities | null;
}

export interface Ringing {
  channelId: number;
  starterName: string;
  channelName: string;
  sinceMs: number;
}

export interface Toast {
  id: number;
  text: string;
  /** Clicking the toast opens this person's card (the request toast). */
  userId: number | null;
}

export interface VoicePeer {
  userId: number;
  senderId: number;
  username: string;
  muted: boolean;
  deafened: boolean;
  serverMuted: boolean;
  rttMs: number;
  screenSharing: boolean;
  screenSharePaused: boolean;
  screenShareHasAudio: boolean;
}

/** The one stream we are watching (F5) — null when none. */
export interface ShareView {
  userId: number;
  paused: boolean;
  hasAudio: boolean;
  /** Share audio's own volume, 0..2 (D99). */
  volume: number;
}

/** Our own live call (F4) — null when not in one. The read-only `calls`
 * map keeps describing every room's call; this slice is the one we are
 * inside, with the state only a participant has. */
export interface VoiceCall {
  channelId: number;
  /** "connecting" until media is up AND the MLS group is usable. */
  phase: "connecting" | "up";
  muted: boolean;
  deafened: boolean;
  /** The R16 verification code — the proof E2EE is unbroken, matched by
   * reading it aloud against another participant's screen. */
  code: string | null;
  peers: VoicePeer[];
  /** Who the SFU currently forwards (top-3), as user ids, self included. */
  speaking: number[];
  rttMs: number;
  lossPct: number;
  concealmentPct: number;
  /** Per-person receive volume, 0..2 (D33); absent = 1. */
  volumes: Record<number, number>;
  share: ShareView | null;
  /** Who each stream is being forwarded to, streamer → viewers, as user ids
   * with ourselves among them while we watch (screenshare-plan.md D336). The
   * relay's word; a stream with no entry is one it has not mentioned, which
   * is not the same as nobody watching. */
  audience: Record<number, number[]>;
}

export interface CardState {
  userId: number | null;
  /** null while the friendship answer flies — the card draws a placeholder,
   * never defaults (a card that briefly offers "Add" to a friend teaches
   * distrust). Never asked for a pet: a pet is nobody's friend (D364). */
  relation: import("../gen/dto").Friendship | null;
  /** A pet's card (pets-plan.md D362), fresh from `GET /v1/pets/{id}` each
   * time the card opens or the pet's revision moves; null while it flies and
   * for a person. */
  pet: PetCard | null;
}

/** A chat's `/` list (pets-plan.md D360), as this session last read it:
 * asked for when the list first opens in the chat, and dropped — to be asked
 * again — when a pet's `membership` frame arrives there or a pet's revision
 * moves past the one it was read at. `none` is a failed read, asked again
 * the next time the list opens. */
export type CommandsEntry =
  | { t: "loading" }
  | { t: "ready"; commands: ChatCommand[] }
  | { t: "none" };

/** How far this session got with a page's card (D280), by the URL as the
 * message spells it. `none` is for good when `retryAt` is null — the gateway
 * had nothing to show, or refused the address, or is older than the route —
 * and otherwise until then: over the fetch budget (429), a fetch that outlived
 * its request (503), a network or a server in trouble. A card draws only
 * `ready`. */
export type LinkPreviewEntry =
  | { t: "loading" }
  | { t: "ready"; preview: LinkPreview }
  | { t: "none"; retryAt: number | null };

/** The same for the card of a channel this reader holds no row of (D281),
 * keyed by `channelCardKey`: a 404 — unknown, not visible, a token that is
 * not the channel's live invite — is `none` for good. */
export type ChannelCardEntry =
  | { t: "loading" }
  | { t: "ready"; card: ChannelCard }
  | { t: "none"; retryAt: number | null };

/** One entry per channel *and* token: the same channel behind a live invite
 * and behind a spent one are two different answers. */
export function channelCardKey(channelId: number, token?: string | null): string {
  return token ? `${channelId}:${token}` : `${channelId}`;
}

/** What the card under a link to a channel draws (D279), flat, so a card can
 * subscribe to it shallowly — from the reader's own row when they hold one,
 * and from `GET /v1/channels/{id}/card` (D281) when they do not. */
export interface ChannelLinkView {
  /** The row is the reader's: the channel can be opened from here. */
  held: boolean;
  kind: string;
  name: string;
  description: string | null;
  avatarFileId: string | null;
  /** The circle's second seed: a direct conversation wears its peer's face,
   * as its sidebar row does; every other channel is seeded by name alone. */
  faceCreatedAt: number;
  members: number;
  member: boolean;
  isPrivate: boolean;
  /** A join from the row's own bar or from a card is in flight. */
  joining: boolean;
}

/** The gateway's clock against this machine's (disappearing-messages.md D298).
 * A deadline is written on the gateway's clock and let go of on this one, and
 * the two can disagree by more than a short lifetime. Each reading is the
 * gateway's clock as something left it, less `Date.now()` as it arrived — the
 * `sent_at_ms` of a message handed over the moment it was stored (a live
 * frame, the answer to a send), or `server_time_ms` of `GET /v1/state`, the
 * reading that comes before the first page — so it is the true offset less
 * the time spent on the way: never above it, and the largest of the last few
 * is the one that travelled fastest. */
export interface ClockState {
  /** The newest `CLOCK_SAMPLES` readings, oldest first. */
  samples: number[];
  /** Their maximum — gateway minus local, ms; 0 while there are none. */
  offsetMs: number;
}

/** What an attachment on its way out counts as on the pill over the composer
 * (chat-plan.md D398): the kind it travels as, so a picture through the
 * «File» door is a file. A voice note raises no pill and has no kind here. */
export type UploadKind = "photo" | "video" | "file";

/** The send whose attachments are going up (D398) — `lib/upload.ts` reads the
 * title, the numbers and the bar out of it. */
export interface UploadState {
  channelId: number;
  /** The send's first nonce — its bubble's. What the cross names, and what a
   * progress tick is matched on, so a late one cannot move the next send's
   * pill. */
  nonce: string;
  /** In the order they go: the picked file's size (the number the sender
   * knows it by), and 0..=1 of its encode and upload together. */
  items: Array<{ kind: UploadKind; bytes: number; progress: number }>;
}

export interface UiState {
  connected: boolean;
  busy: boolean;
  /** THE one transient error line. */
  notice: string | null;
  /** Signed in, but the gateway opens nothing until this account proves an
   * e-mail address (D311, `GET /v1/me.must_confirm_email`). While it is set
   * the window shows the confirmation card instead of the app, and no socket
   * is opened — the gateway would refuse it. */
  mustConfirmEmail: boolean;
  selfUserId: number;
  selfUsername: string;
  directory: DirectoryState;
  rooms: Room[];
  selectedChannel: number | null;
  tab: SidebarTab;
  feed: FeedState;
  friends: FriendsState;
  presence: PresenceState;
  account: AccountState;
  calls: Record<number, Call>;
  voice: VoiceCall | null;
  ringing: Ringing | null;
  card: CardState;
  toasts: Toast[];
  /** Link cards (D280, D281): what the gateway answered, for this session. */
  linkPreviews: Record<string, LinkPreviewEntry>;
  channelCards: Record<string, ChannelCardEntry>;
  /** The composer's clock (D298): channel id → the lifetime, in seconds, that
   * whatever is sent there next goes out under; absent — it stays. Here and
   * not in the composer, because a voice note and a dropped file are sent
   * from outside the box and go under the same clock. For the session and no
   * longer: never stored, gone with the rest of the state at sign-out, and
   * nothing the gateway hears of except as `ttl_s` on a send. */
  sendTtl: Record<number, number>;
  clock: ClockState;
  /** Channel id → its `/` list (pets-plan.md D360), for the session. */
  commands: Record<number, CommandsEntry>;
  /** The forward waiting over a composer (forwarding.md D377) — one at a
   * time, as Telegram's bar: aiming another replaces it. Here and not on the
   * feed, which a channel switch resets: the forward is what opens the chat
   * it goes to. */
  forward: ForwardDraft | null;
  /** The send whose attachments are going up, while they are (chat-plan.md
   * D398) — what the pill over that chat's composer draws. At most one: the
   * send queue runs one send at a time. Here and not on the feed, which a
   * channel switch resets. */
  upload: UploadState | null;
  /** The words of a send called off from the pill (D398), or of an edit the
   * gateway did not take, on their way back to the box of `channelId`: its
   * composer takes them if it is empty and lets them go if something new is
   * typed there. */
  returnedDraft: { channelId: number; text: string } | null;
}

export function initialState(): UiState {
  return {
    connected: false,
    busy: false,
    notice: null,
    mustConfirmEmail: false,
    selfUserId: 0,
    selfUsername: "",
    directory: {
      people: {},
      selfId: 0,
      selfIdFallback: 0,
      lastRefreshMs: null,
      refreshWanted: false,
    },
    rooms: [],
    selectedChannel: null,
    tab: "all",
    feed: emptyFeed(),
    friends: {
      friends: [],
      incoming: [],
      outgoing: [],
      blocked: [],
      policy: "everyone",
      seeded: false,
    },
    presence: { map: {}, selfAfk: false, enabled: false },
    account: {
      displayName: null,
      bio: null,
      avatarFileId: null,
      createdAt: 0,
      bubbleLight: null,
      bubbleDark: null,
      manualStatus: null,
      isAdmin: false,
      pets: null,
    },
    calls: {},
    voice: null,
    ringing: null,
    card: { userId: null, relation: null, pet: null },
    toasts: [],
    linkPreviews: {},
    channelCards: {},
    sendTtl: {},
    clock: { samples: [], offsetMs: 0 },
    commands: {},
    forward: null,
    upload: null,
    returnedDraft: null,
  };
}
